import ChartTemplate from "@/components/chart-template";
import { ClientMetricsRow } from "@/http/api-types";
import { formatDateToMYYYY } from "@/lib/utils";
import { subMonths } from "date-fns";
import GenericClientChart from "./generic-client-chart";

type ActiveClientsChartProps = {
  audienceData: ClientMetricsRow[];
  type?: "client" | "audience";
};

function ActiveClients({ audienceData, type = "client" }: ActiveClientsChartProps) {
  const isAudience = type === "audience";
  const label = isAudience ? "Audience" : "Client";
  const labelPlural = isAudience ? "Audiences" : "Clients";

  const activeNames = audienceData.reduce(
    (acc, row) => {
      acc[row.month_year] = isAudience
        ? row.active_audiences?.map(item => item.name) || []
        : row.active_organizations?.map(item => item.name) || [];
      return acc;
    },
    {} as { [key: string]: string[] },
  );

  const newNames = audienceData.reduce(
    (acc, row) => {
      acc[row.month_year] = isAudience
        ? row.new_audiences?.map(item => item.name) || []
        : row.new_clients?.map(item => item.name) || [];
      return acc;
    },
    {} as { [key: string]: string[] },
  );

  const chartData = audienceData
    .map(row => {
      const activeItems = isAudience ? row.active_audiences : row.active_organizations;
      const highPerfItems = isAudience ? row.high_performance_audiences : row.high_performance_organizations;

      return {
        month_year: row.month_year,
        dateTime: new Date(row.month_year).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        }),
        clients: activeItems?.length || 0,
        activeAudiences: activeItems?.length || 0,
        highPerformanceAudiences: highPerfItems?.length || 0,
        activeOrganizationNames: activeItems?.map(item => item.name) || [],
        highPerformanceOrganizationNames: highPerfItems?.map(item => item.name) || [],
      };
    })
    .sort((a, b) => (a.month_year > b.month_year ? 1 : -1));

  const lastMonth = formatDateToMYYYY(subMonths(new Date(), 1));
  const lastMonthData = audienceData.find(row => row.month_year === lastMonth);
  const activeCount = isAudience
    ? lastMonthData?.active_audiences?.length || 0
    : lastMonthData?.active_organizations?.length || 0;

  return (
    <ChartTemplate
      timeSplittable={false}
      summaryMetric={`${activeCount}`}
      summaryMetricLabel={`Active ${labelPlural} Last Month`}
      title={`Active ${labelPlural}`}
      description={`${labelPlural} who have spent more than 500€`}
      chart={
        <GenericClientChart
          chartData={chartData}
          clientNames={activeNames}
          newClientNames={newNames}
          showAudienceMetrics={true}
        />
      }
      table={null}
    ></ChartTemplate>
  );
}

export default ActiveClients;
